import * as React from "react";
import { Command as Cmd } from "cmdk";
import { Check, ChevronDown, X } from "lucide-react";

import { Button } from "../button";
import { Popover, PopoverContent, PopoverTrigger } from "../popover";
import { cn } from "../../lib/utils";

export type SearchSelectOption<T = unknown> = {
  value: string;
  label: string;
  disabled?: boolean;
  data?: T;
};

type SearchSelectContextValue = {
  disabled: boolean;
  readonly: boolean;
  isInteractive: boolean;
  close: () => void;
};

const SearchSelectContext = React.createContext<SearchSelectContextValue | null>(null);

const useSearchSelectContext = () => {
  const context = React.useContext(SearchSelectContext);
  if (!context) {
    throw new Error("SearchSelect components must be used within SearchSelectRoot.");
  }

  return context;
};

export type SearchSelectRootProps = React.PropsWithChildren<{
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
  readonly?: boolean;
}>;

export function SearchSelectRoot({
  children,
  open,
  defaultOpen = false,
  onOpenChange,
  disabled = false,
  readonly = false,
}: SearchSelectRootProps) {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
  const isControlled = typeof open === "boolean";
  const isInteractive = !disabled && !readonly;
  const actualOpen = isInteractive && (isControlled ? open : internalOpen);

  const setOpen = React.useCallback(
    (nextOpen: boolean) => {
      if (!isInteractive) return;
      if (!isControlled) setInternalOpen(nextOpen);
      onOpenChange?.(nextOpen);
    },
    [isControlled, isInteractive, onOpenChange],
  );

  const contextValue = React.useMemo(
    () => ({
      disabled,
      readonly,
      isInteractive,
      close: () => setOpen(false),
    }),
    [disabled, isInteractive, readonly, setOpen],
  );

  return (
    <SearchSelectContext.Provider value={contextValue}>
      <Popover
        open={actualOpen}
        onOpenChange={setOpen}
      >
        {children}
      </Popover>
    </SearchSelectContext.Provider>
  );
}

export type SearchSelectTriggerProps = Omit<
  React.ComponentPropsWithoutRef<typeof Button>,
  "asChild" | "disabled"
>;

export const SearchSelectTrigger = React.forwardRef<HTMLButtonElement, SearchSelectTriggerProps>(
  ({ className, type = "button", ...props }, ref) => {
    const { disabled, readonly } = useSearchSelectContext();

    return (
      <PopoverTrigger asChild>
        <Button
          ref={ref}
          type={type}
          variant="outline"
          disabled={disabled || readonly}
          aria-readonly={readonly || undefined}
          className={cn(
            "relative flex min-h-10 w-full items-center justify-between gap-2 rounded-md border-gray-200 bg-white px-3 py-2 text-left hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700",
            "disabled:cursor-not-allowed disabled:bg-[#F3F3F5] disabled:opacity-50 dark:disabled:bg-gray-800",
            className,
          )}
          {...props}
        />
      </PopoverTrigger>
    );
  },
);

SearchSelectTrigger.displayName = "SearchSelectTrigger";

export type SearchSelectValueProps = {
  value?: React.ReactNode;
  placeholder: React.ReactNode;
  className?: string;
};

export function SearchSelectValue({ value, placeholder, className }: SearchSelectValueProps) {
  const hasValue = value !== undefined && value !== null && value !== "";

  return (
    <span
      className={cn(
        "min-w-0 flex-1 truncate text-sm md:text-sm",
        hasValue
          ? "text-foreground dark:text-gray-100"
          : "text-muted-foreground dark:text-gray-400",
        className,
      )}
    >
      {hasValue ? value : placeholder}
    </span>
  );
}

export const SearchSelectChevron = () => (
  <ChevronDown
    className="h-4 w-4 shrink-0 text-muted-foreground"
    size={16}
  />
);

export type SearchSelectClearProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "type" | "onClick"
> & {
  onClear: () => void;
};

export const SearchSelectClear = React.forwardRef<HTMLButtonElement, SearchSelectClearProps>(
  ({ className, onClear, onPointerDown, ...props }, ref) => {
    const { isInteractive } = useSearchSelectContext();

    return (
      <button
        ref={ref}
        type="button"
        aria-label="Clear"
        title="Clear"
        disabled={!isInteractive}
        className={cn(
          "absolute right-8 top-1/2 z-10 -translate-y-1/2 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100",
          className,
        )}
        onPointerDown={(event) => {
          onPointerDown?.(event);
          if (event.defaultPrevented) return;
          event.preventDefault();
          event.stopPropagation();
        }}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onClear();
        }}
        {...props}
      >
        <X className="h-4 w-4" />
      </button>
    );
  },
);

SearchSelectClear.displayName = "SearchSelectClear";

export const SearchSelectContent = React.forwardRef<
  React.ElementRef<typeof PopoverContent>,
  React.ComponentPropsWithoutRef<typeof PopoverContent>
>(({ className, onOpenAutoFocus, ...props }, ref) => (
  <PopoverContent
    ref={ref}
    className={cn(
      "w-[--radix-popover-trigger-width] border border-gray-200 bg-white p-0 dark:border-gray-700 dark:bg-gray-800",
      className,
    )}
    onOpenAutoFocus={(event) => {
      event.preventDefault();
      onOpenAutoFocus?.(event);
    }}
    {...props}
  />
));

SearchSelectContent.displayName = "SearchSelectContent";

export const SearchSelectCommand = React.forwardRef<
  React.ElementRef<typeof Cmd>,
  React.ComponentPropsWithoutRef<typeof Cmd>
>(({ className, ...props }, ref) => (
  <Cmd
    ref={ref}
    className={className}
    {...props}
  />
));

SearchSelectCommand.displayName = "SearchSelectCommand";

export const SearchSelectInput = React.forwardRef<
  React.ElementRef<typeof Cmd.Input>,
  React.ComponentPropsWithoutRef<typeof Cmd.Input>
>(({ className, ...props }, ref) => (
  <Cmd.Input
    ref={ref}
    autoFocus
    className={cn(
      "h-10 w-full border-b border-gray-200 bg-white px-3 text-sm text-gray-900 outline-none placeholder:text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100",
      className,
    )}
    {...props}
  />
));

SearchSelectInput.displayName = "SearchSelectInput";

export const SearchSelectList = React.forwardRef<
  React.ElementRef<typeof Cmd.List>,
  React.ComponentPropsWithoutRef<typeof Cmd.List>
>(({ className, ...props }, ref) => (
  <Cmd.List
    ref={ref}
    className={cn("max-h-60 overflow-y-auto p-1", className)}
    {...props}
  />
));

SearchSelectList.displayName = "SearchSelectList";

export const SearchSelectEmpty = React.forwardRef<
  React.ElementRef<typeof Cmd.Empty>,
  React.ComponentPropsWithoutRef<typeof Cmd.Empty>
>(({ className, ...props }, ref) => (
  <Cmd.Empty
    ref={ref}
    className={cn("py-6 text-center text-sm text-gray-500 dark:text-gray-400", className)}
    {...props}
  />
));

SearchSelectEmpty.displayName = "SearchSelectEmpty";

export const SearchSelectGroup = React.forwardRef<
  React.ElementRef<typeof Cmd.Group>,
  React.ComponentPropsWithoutRef<typeof Cmd.Group>
>(({ className, ...props }, ref) => (
  <Cmd.Group
    ref={ref}
    className={className}
    {...props}
  />
));

SearchSelectGroup.displayName = "SearchSelectGroup";

export type SearchSelectItemProps = React.ComponentPropsWithoutRef<typeof Cmd.Item> & {
  closeOnSelect?: boolean;
};

export const SearchSelectItem = React.forwardRef<
  React.ElementRef<typeof Cmd.Item>,
  SearchSelectItemProps
>(({ className, closeOnSelect = false, onClick, onSelect, value, ...props }, ref) => {
  const { close } = useSearchSelectContext();
  const hasSelectedRef = React.useRef(false);

  const handleSelect = React.useCallback(
    (selectedValue: string) => {
      if (hasSelectedRef.current) return;
      hasSelectedRef.current = true;
      onSelect?.(selectedValue);
      if (closeOnSelect) close();

      queueMicrotask(() => {
        hasSelectedRef.current = false;
      });
    },
    [close, closeOnSelect, onSelect],
  );

  return (
    <Cmd.Item
      ref={ref}
      value={value}
      className={cn(
        "flex cursor-pointer items-center gap-2 rounded-sm px-3 py-1.5 text-sm text-gray-900 aria-selected:bg-gray-100  dark:text-gray-100 dark:aria-selected:bg-gray-700",
        className,
      )}
      onSelect={handleSelect}
      onClick={(event) => {
        onClick?.(event);
        if (event.defaultPrevented || typeof value !== "string") return;
        handleSelect(value);
      }}
      {...props}
    />
  );
});

SearchSelectItem.displayName = "SearchSelectItem";

export type SearchSelectOptionItemProps<T = unknown> = {
  option: SearchSelectOption<T>;
  isSelected: boolean;
  onSelect: (option: SearchSelectOption<T>) => void;
  closeOnSelect?: boolean;
};

export function SearchSelectOptionItem<T = unknown>({
  option,
  isSelected,
  onSelect,
  closeOnSelect,
}: SearchSelectOptionItemProps<T>) {
  return (
    <SearchSelectItem
      value={`${option.value} ${option.label}`}
      disabled={option.disabled}
      closeOnSelect={closeOnSelect}
      onSelect={() => onSelect(option)}
    >
      <span
        className={cn(
          "flex h-4 w-4 shrink-0 items-center justify-center",
          isSelected ? "text-emerald-600 dark:text-emerald-400" : "text-transparent",
        )}
      >
        <Check className="h-4 w-4" />
      </span>
      <span className="truncate">{option.label}</span>
    </SearchSelectItem>
  );
}

export type SearchSelectProps<T = unknown> = {
  value: string;
  onChange: (value: string) => void;
  options: SearchSelectOption<T>[];
  placeholder?: string;
  ariaLabel?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  triggerClassName?: string;
  contentClassName?: string;
  disabled?: boolean;
  readonly?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onClear?: () => void;
  showClear?: boolean;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  sideOffset?: number;
};

export function SearchSelect<T = unknown>({
  value,
  onChange,
  options,
  placeholder = "Select...",
  ariaLabel,
  searchPlaceholder = "Search...",
  emptyMessage = "No results.",
  className,
  triggerClassName,
  contentClassName,
  disabled = false,
  readonly = false,
  open,
  onOpenChange,
  onClear,
  showClear = true,
  side = "bottom",
  align = "start",
  sideOffset = 8,
}: SearchSelectProps<T>) {
  const selected = React.useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );

  return (
    <div className={cn("w-full", className)}>
      <SearchSelectRoot
        open={open}
        onOpenChange={onOpenChange}
        disabled={disabled}
        readonly={readonly}
      >
        <div className="relative w-full">
          <SearchSelectTrigger
            aria-label={ariaLabel}
            className={triggerClassName}
          >
            <SearchSelectValue
              value={selected?.label}
              placeholder={placeholder}
            />
            <SearchSelectChevron />
          </SearchSelectTrigger>
          {showClear && selected ? (
            <SearchSelectClear onClear={onClear ?? (() => onChange(""))} />
          ) : null}
        </div>
        <SearchSelectContent
          side={side}
          align={align}
          sideOffset={sideOffset}
          className={contentClassName}
        >
          <SearchSelectCommand>
            <SearchSelectInput
              placeholder={searchPlaceholder}
              aria-label={ariaLabel ?? searchPlaceholder}
            />
            <SearchSelectList>
              <SearchSelectEmpty>{emptyMessage}</SearchSelectEmpty>
              <SearchSelectGroup>
                {options.map((option) => (
                  <SearchSelectOptionItem
                    key={option.value}
                    option={option}
                    isSelected={option.value === value}
                    onSelect={(selectedOption) => onChange(selectedOption.value)}
                    closeOnSelect
                  />
                ))}
              </SearchSelectGroup>
            </SearchSelectList>
          </SearchSelectCommand>
        </SearchSelectContent>
      </SearchSelectRoot>
    </div>
  );
}
